import { readStorage, writeStorage } from "../browserStorage.js";
import { useEffect, useState } from "react";

const consentKey = "aidbiz_analytics_consent";
const privatePath = (path) =>
  path.startsWith("/admin") ||
  path.startsWith("/apply") ||
  path.startsWith("/auth");

export default function AnalyticsTracker({ path }) {
  const [consentVersion, setConsentVersion] = useState(0);
  useEffect(() => {
    const changed = () => setConsentVersion(value => value + 1);
    const stored = event => { if (event.key === consentKey || event.key === null) changed(); };
    window.addEventListener("aidbiz:consent-change", changed);
    window.addEventListener("storage", stored);
    return () => { window.removeEventListener("aidbiz:consent-change", changed); window.removeEventListener("storage", stored); };
  }, []);
  useEffect(() => {
    const endpoint = import.meta.env.VITE_PUBLIC_API_URL;
    if (
      !endpoint ||
      privatePath(path) ||
      navigator.doNotTrack === "1" ||
      navigator.globalPrivacyControl === true ||
      readStorage("localStorage", consentKey) !== "granted"
    )
      return;
    const get = (store, key, fallback) => {
      let value = readStorage(store, key);
      if (!value) {
        value = fallback();
        if (!writeStorage(store, key, value)) return null;
      }
      return value;
    };
    const visitorId = get("localStorage", "aidbiz_analytics_visitor", () =>
      crypto.randomUUID(),
    );
    const sessionId = get("sessionStorage", "aidbiz_analytics_session", () =>
      crypto.randomUUID(),
    );
    const landingPath = get(
      "sessionStorage",
      "aidbiz_analytics_landing",
      () => path,
    );
    if (!visitorId || !sessionId || !landingPath) return;
    const query = new URLSearchParams(location.search);
    const attribution = {
      landingPath,
      referrer: document.referrer || null,
      utmSource: query.get("utm_source"),
      utmMedium: query.get("utm_medium"),
      utmCampaign: query.get("utm_campaign"),
      utmContent: query.get("utm_content"),
      utmTerm: query.get("utm_term"),
    };
    const deviceType =
      innerWidth < 640 ? "mobile" : innerWidth < 1024 ? "tablet" : "desktop";
    const controller = new AbortController();
    const stop = () => controller.abort();
    const stored = event => { if (event.key === consentKey || event.key === null) stop(); };
    window.addEventListener("aidbiz:consent-change", stop);
    window.addEventListener("storage", stored);
    const record = (eventType, details = {}) => {
      if (controller.signal.aborted || readStorage("localStorage", consentKey) !== "granted" || navigator.doNotTrack === "1" || navigator.globalPrivacyControl === true || privatePath(location.pathname)) return;
      return fetch(`${endpoint}/track-event`, {
        method: "POST",
        signal: controller.signal,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          eventType,
          path,
          visitorId,
          sessionId,
          consent: "granted",
          deviceType,
          attribution,
          ...details,
        }),
      }).catch(() => undefined);
    };
    record("page_view");
    const started = new WeakSet();
    const onFocus = (event) => {
      const form = event.target?.closest?.("form[data-analytics-form]");
      if (form && !started.has(form)) {
        started.add(form);
        record("form_start", { actionName: form.dataset.analyticsForm });
      }
    };
    const onClick = (event) => {
      const action = event.target?.closest?.("[data-analytics-action]")?.dataset
        .analyticsAction;
      if (action)
        record("cta_click", {
          actionName: action,
          xPermille: Math.round(
            (event.clientX / Math.max(innerWidth, 1)) * 1000,
          ),
          yPermille: Math.round(
            (event.clientY / Math.max(innerHeight, 1)) * 1000,
          ),
        });
    };
    document.addEventListener("focusin", onFocus);
    document.addEventListener("click", onClick);
    const sentDepths = new Set();
    const onScroll = () => {
      const available = Math.max(document.documentElement.scrollHeight - innerHeight, 1);
      const depth = Math.min(100, Math.round((scrollY / available) * 100));
      for (const threshold of [25, 50, 75, 100]) if (depth >= threshold && !sentDepths.has(threshold)) { sentDepths.add(threshold); record("scroll_depth", { scrollDepth: threshold }); }
    };
    document.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      stop();
      window.removeEventListener("aidbiz:consent-change", stop);
      window.removeEventListener("storage", stored);
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("click", onClick);
      document.removeEventListener("scroll", onScroll);
    };
  }, [path, consentVersion]);
  return null;
}
